import { useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { toast } from "@volca/ui/lib/toast";
import { usePushInvoiceToQuickbooks } from "@/accounts-payable/query";
import { quickbooksPushToast } from "@/accounts-payable/quickbooks-bill";
import type { ApInvoice } from "@/accounts-payable/types";
import { getErrorMessage } from "@/core/api/errors";
import { QuickbooksBillUpdateDialog } from "./quickbooks-bill-update-dialog";
import { useQuickbooksBillPush } from "./use-quickbooks-bill-push";

/**
 * "Push to QuickBooks", or "Update bill in QuickBooks" once the bill is live
 * (after a confirmation, since it rewrites the bill). Disabled when the API
 * says it can't go now; the reason is in `QuickbooksBillPushNote`, which must
 * render alongside it. Nothing for a business without QuickBooks bills, or
 */
export const QuickbooksBillPushButton = ({
  invoice,
  businessId,
  appearance = "button",
  className,
}: {
  invoice: Pick<ApInvoice, "id" | "quickbooks_bill">;
  businessId: string | null;
  appearance?: "button" | "link";
  className?: string;
}) => {
  const { ready, pushable, mode, label, note, noteId } = useQuickbooksBillPush(
    invoice,
    businessId,
  );
  // Here rather than in the dialog: `mutate` callbacks only run while their
  // observer is mounted.
  const push = usePushInvoiceToQuickbooks(businessId);
  const [confirming, setConfirming] = useState(false);

  if (!ready || !label) return null;

  const send = () =>
    push.mutate(invoice.id, {
      onSuccess: (action) => {
        const { title, description } = quickbooksPushToast(action);
        toast.success(title, { description });
      },
      onError: (error) =>
        toast.error(
          mode === "update"
            ? "Couldn’t update the bill in QuickBooks"
            : "Couldn’t push to QuickBooks",
          {
            description: getErrorMessage(error),
          },
        ),
      onSettled: () => setConfirming(false),
    });

  const trigger = {
    className,
    disabled: !pushable || push.isPending,
    "aria-describedby": !pushable && note ? noteId : undefined,
    onClick: () => (mode === "update" ? setConfirming(true) : send()),
  };

  return (
    <>
      {appearance === "link" ? (
        <Button variant="link" {...trigger}>
          {label}
        </Button>
      ) : (
        <Button
          variant="secondary"
          aria-busy={push.isPending && !confirming}
          {...trigger}
        >
          {label}
        </Button>
      )}
      {mode === "update" && (
        <QuickbooksBillUpdateDialog
          open={confirming}
          onClose={() => setConfirming(false)}
          onConfirm={send}
          isPending={push.isPending}
        />
      )}
    </>
  );
};
